class BellyfulPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.status = this.querySelector('[data-predictive-search-status]');
    this.timer = null;
    this.abortController = null;
    this.bind();
  }

  bind() {
    this.input?.addEventListener('input', () => {
      clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.clear();
        return;
      }
      this.timer = setTimeout(() => this.search(query), 160);
    });
  }

  async search(query) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setStatus('Searching…');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });
      const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Search unavailable');
      const payload = await response.json();
      this.render(payload.resources?.results || {}, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.setStatus('Search is temporarily unavailable.');
    }
  }

  render(groups, query) {
    if (!this.results) return;
    this.results.replaceChildren();
    let count = 0;
    const hidden = new Set((window.BellyfulSecondaryProducts || []).map(String));
    groups = {...groups, products:(groups.products || []).filter(item => !hidden.has(String(item.id)))};

    const productGroup = this.createGroup('Products', groups.products || [], (item) => {
      const link = document.createElement('a');
      link.className = 'bf-search-product';
      link.href = item.url;

      const imageUrl = item.featured_image?.url || item.featured_image || item.image?.url || item.image;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        link.appendChild(image);
      }

      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = item.title;
      copy.appendChild(title);
      if (item.price) {
        const price = document.createElement('small');
        price.textContent = item.price;
        copy.appendChild(price);
      }
      link.appendChild(copy);
      return link;
    });

    if (productGroup) {
      count += (groups.products || []).length;
      this.results.appendChild(productGroup);
    }

    const simpleGroups = [
      ['Collections', groups.collections || []],
      ['Pages', groups.pages || []],
      ['Stories', groups.articles || []]
    ];

    simpleGroups.forEach(([label, items]) => {
      const group = this.createGroup(label, items, (item) => {
        const link = document.createElement('a');
        link.className = 'bf-search-link';
        link.href = item.url;
        link.textContent = item.title;
        return link;
      });
      if (group) {
        count += items.length;
        this.results.appendChild(group);
      }
    });

    if (!count) {
      const empty = document.createElement('p');
      empty.className = 'bf-search-empty';
      empty.textContent = `No suggestions for “${query}”.`;
      this.results.appendChild(empty);
    }

    const fullSearch = document.createElement('a');
    fullSearch.className = 'bf-search-all';
    fullSearch.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}`;
    fullSearch.textContent = `See all results for “${query}”`;
    this.results.appendChild(fullSearch);
    this.setStatus(count ? `${count} suggestions available.` : 'No suggestions found.');
  }

  createGroup(label, items, renderer) {
    if (!items.length) return null;
    const section = document.createElement('section');
    section.className = 'bf-search-group';
    const heading = document.createElement('h2');
    heading.textContent = label;
    section.appendChild(heading);
    const list = document.createElement('div');
    items.forEach((item) => list.appendChild(renderer(item)));
    section.appendChild(list);
    return section;
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  clear() {
    this.abortController?.abort();
    this.results?.replaceChildren();
    this.setStatus('Type at least two characters.');
  }
}

if (!customElements.get('bellyful-predictive-search')) {
  customElements.define('bellyful-predictive-search', BellyfulPredictiveSearch);
}
